/**
 * NewImageWizardPage — guided „Neues Image"-Stepper (NC-972, Phase 3).
 *
 * Replaces the 5-loose-steps-over-3-pages self-service flow (UX audit #971)
 * with one guided stepper. Built incrementally behind NC_972_WIZARD_ENABLED:
 *   - PR-2 (this): step 1 (workload) + step 2 (readiness). Steps 3–5 stubbed.
 *   - PR-3/PR-4: build dispatch, status polling, promotion.
 *
 * Reuse (no duplicated logic — AC): isBuildable (#984), evaluateBuildReadiness
 * + BuildReadinessChecklist (#983), imageReadinessApi (#967). The wizard is the
 * forward-navigation the audit found missing.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Checkbox,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { workloadsApi } from '../services/workloadsService';
import { imageReadinessApi, WorkloadReadiness } from '../services/imageReadinessService';
import { imageBuildsApi } from '../services/imageBuildsService';
import { costCentersApi } from '../services/costCentersService';
import { CostCenterDefinition } from '../services/types';
import { isBuildable, isPromotable, isStagingOnly } from '../config/workloadCapabilities';
import { evaluateBuildReadiness } from '../utils/workloadReadiness';
import BuildReadinessChecklist from '../components/workload/BuildReadinessChecklist';
import { useApiQuery } from '../hooks/useApiQuery';
import { useAuth } from '../contexts/AuthContext';
import { TableSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';

const STEP_LABELS = ['Workload', 'Readiness', 'Build', 'Status', 'Promotion'];

export default function NewImageWizardPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedWorkloadId, setSelectedWorkloadId] = useState('');
  const [readinessOverride, setReadinessOverride] = useState(false);

  // Step 3 (Build) — dispatch state.
  const [dryRun, setDryRun] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [dispatched, setDispatched] = useState<{ buildId: string; jobUrl: string | null } | null>(null);

  // NC-867 T5: mandatory owner cost-center + optional purpose for the built image.
  const [costCenters, setCostCenters] = useState<CostCenterDefinition[]>([]);
  const [ownerCostCenter, setOwnerCostCenter] = useState('');
  const [purpose, setPurpose] = useState('');

  useEffect(() => {
    let cancelled = false;
    costCentersApi
      .list({ active: true })
      .then((data) => {
        if (cancelled) return;
        setCostCenters(data);
        setOwnerCostCenter((prev) => (prev === '' && data.length === 1 ? data[0].code : prev));
      })
      .catch(() => {
        if (!cancelled) setCostCenters([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: workloads, loading: workloadsLoading, error: workloadsError } = useApiQuery(
    () => workloadsApi.list(),
    []
  );

  const buildableWorkloads = useMemo(
    () => (workloads ?? []).filter((w) => isBuildable(w.id)),
    [workloads]
  );

  const selectedWorkload = useMemo(
    () => buildableWorkloads.find((w) => w.id === selectedWorkloadId),
    [buildableWorkloads, selectedWorkloadId]
  );

  // Live installer readiness (#967) — only once a workload is picked.
  const {
    data: readiness,
    loading: readinessLoading,
    error: readinessError,
  } = useApiQuery<WorkloadReadiness>(
    () => imageReadinessApi.get(selectedWorkloadId),
    [selectedWorkloadId],
    { enabled: !!selectedWorkloadId }
  );

  // Local, config-level readiness (#983) — derived from the selected workload.
  const localReadiness = selectedWorkload
    ? evaluateBuildReadiness({
        applicationsCount: (selectedWorkload.applications ?? []).length,
        baseImage: selectedWorkload.packerConfig.baseImage,
        templateFile: selectedWorkload.packerConfig.templateFile,
        variablesFile: selectedWorkload.packerConfig.variablesFile,
      })
    : null;

  // Combined gate: config essentials AND live installers must be ready. A live
  // fetch error fails open (advisory) — the BE dispatch gate still enforces.
  const installersReady = !readiness || readiness.ready;
  const combinedReady = !!localReadiness && localReadiness.allReady && installersReady;

  const handleNext = () => setActiveStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  // Step 3 — dispatch the build (reuses imageBuildsApi.build; the BE re-checks
  // readiness and 400s if the workload isn't ready). On success, advance to the
  // Status step which hands off to the existing detail page for live polling.
  const handleBuild = async () => {
    setSubmitting(true);
    setBuildError(null);
    try {
      const result = await imageBuildsApi.build({
        workloadId: selectedWorkloadId,
        dryRun,
        ownerCostCenter,
        ...(purpose.trim() ? { purpose: purpose.trim() } : {}),
      });
      setDispatched({ buildId: result.buildId, jobUrl: result.jobUrl });
      setActiveStep(3);
    } catch (err) {
      setBuildError(err instanceof Error ? err.message : 'Build konnte nicht gestartet werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextDisabled =
    (activeStep === 0 && !selectedWorkloadId) ||
    (activeStep === 1 && !combinedReady && !readinessOverride);

  // Footer navigation. "Weiter" advances the pure-nav steps: workload/readiness
  // (0/1) and, after dispatch, Status→Promotion (3→4). Step 2 (Build) has its
  // own action button; step 4 (Promotion) ends with "Fertig". Back is hidden
  // once a build has been dispatched (re-dispatch makes no sense).
  const showNext = activeStep <= 1 || (activeStep === 3 && !!dispatched);
  const showBack = activeStep > 0 && !dispatched;

  if (workloadsLoading) {
    return <TableSkeleton rows={6} columns={2} />;
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          Neues Image
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/workloads')}>
          Abbrechen
        </Button>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {workloadsError && <ErrorMessage message={workloadsError} title="Fehler" />}

      <Card>
        <CardContent>
          {/* Step 1 — Workload wählen */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Workload wählen
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Wähle den Workload, für den ein Image gebaut werden soll. Nur build-fähige
                Workloads werden angezeigt.
              </Typography>
              {buildableWorkloads.length === 0 ? (
                <Alert severity="info">Keine build-fähigen Workloads vorhanden.</Alert>
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="wizard-workload-label">Workload</InputLabel>
                  <Select
                    labelId="wizard-workload-label"
                    label="Workload"
                    value={selectedWorkloadId}
                    onChange={(e) => {
                      setSelectedWorkloadId(e.target.value as string);
                      setReadinessOverride(false);
                    }}
                  >
                    {buildableWorkloads.map((w) => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name} ({w.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {/* Step 2 — Readiness */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Build-Readiness
              </Typography>

              {localReadiness && (
                <BuildReadinessChecklist
                  checks={localReadiness.checks}
                  allReady={localReadiness.allReady}
                />
              )}

              <Box sx={{ mt: 2 }} data-testid="installer-readiness">
                <Typography variant="subtitle2" gutterBottom>
                  Installer-Status
                </Typography>
                {readinessLoading && (
                  <Alert severity="info" icon={<CircularProgress size={16} />}>
                    <Typography variant="body2">Prüfe Installer-Readiness…</Typography>
                  </Alert>
                )}
                {!readinessLoading && readinessError && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      Installer-Readiness konnte nicht geprüft werden — der Server prüft sie beim
                      Build erneut.
                    </Typography>
                  </Alert>
                )}
                {!readinessLoading && readiness && readiness.ready && (
                  <Alert severity="success">
                    <Typography variant="body2">Alle deklarierten Installer sind bereit.</Typography>
                  </Alert>
                )}
                {!readinessLoading && readiness && !readiness.ready && (
                  <Alert severity="warning">
                    <Typography variant="body2" fontWeight="bold">
                      Fehlende Installer:
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {readiness.apps
                        .filter((a) => !a.ready)
                        .map((a) => (
                          <li key={a.applicationId}>
                            <Typography variant="body2" component="span">
                              {a.displayName}: {a.reason || 'kein validierter Installer'}
                            </Typography>
                          </li>
                        ))}
                    </Box>
                  </Alert>
                )}
              </Box>

              {!combinedReady && (
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Checkbox
                      checked={readinessOverride}
                      onChange={(e) => setReadinessOverride(e.target.checked)}
                    />
                  }
                  label="Trotz offener Punkte fortfahren (der Build kann fehlschlagen)"
                />
              )}
            </Box>
          )}

          {/* Step 3 — Build dispatch */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Build starten
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ⏱️ Dauer ca. 60–90 Minuten. 💶 Kosten ca. €5–10 pro Lauf. Der Build läuft gegen
                  die staging-Gallery.
                </Typography>
              </Alert>
              {isStagingOnly(selectedWorkloadId) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Hinweis: {selectedWorkloadId}-Images bleiben in staging und sind nicht
                    promotbar.
                  </Typography>
                </Alert>
              )}
              {/* NC-867 T5: mandatory owner cost-center + optional purpose. */}
              <TextField
                select
                fullWidth
                required
                label="Kostenstelle (Owner)"
                value={ownerCostCenter}
                onChange={(e) => setOwnerCostCenter(e.target.value)}
                sx={{ mb: 2 }}
                helperText={
                  costCenters.length === 0
                    ? 'Keine aktive Kostenstelle verfügbar — bitte zuerst eine Kostenstelle anlegen'
                    : 'Pflichtfeld — das Image wird dieser Kostenstelle zugeordnet'
                }
              >
                {costCenters.map((cc) => (
                  <MenuItem key={cc.code} value={cc.code}>
                    {cc.displayName} ({cc.code})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Zweck (optional)"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 500 }}
                helperText="Wozu wird dieses Image gebaut? (optional, max. 500 Zeichen)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                  />
                }
                label="Validate-only (Dry-Run, kein Image-Capture)"
              />
              {buildError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {buildError}
                </Alert>
              )}
            </Box>
          )}

          {/* Step 4 — Status (hands off to the detail page for live polling) */}
          {activeStep === 3 && dispatched && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Build läuft
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Build <strong>{dispatched.buildId.slice(0, 8)}</strong> wurde gestartet
                  {dryRun ? ' (Dry-Run)' : ''}. Verfolge den Fortschritt auf der Status-Seite.
                </Typography>
              </Alert>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  onClick={() =>
                    navigate(
                      `/images/${dispatched.buildId}?workloadId=${encodeURIComponent(selectedWorkloadId)}`
                    )
                  }
                >
                  Status ansehen
                </Button>
                {dispatched.jobUrl && (
                  <Button variant="outlined" href={dispatched.jobUrl} target="_blank" rel="noopener noreferrer">
                    GitHub-Run
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Step 5 — Promotion info (role-aware; actual promotion on the detail page) */}
          {activeStep === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Promotion
              </Typography>
              {isStagingOnly(selectedWorkloadId) ? (
                <Alert severity="info">
                  {selectedWorkloadId}-Images bleiben in staging und werden nicht nach production
                  promotet.
                </Alert>
              ) : isPromotable(selectedWorkloadId) && hasRole('admin') ? (
                <Alert severity="info">
                  Nach erfolgreichem Build kannst du das Image auf der Status-Seite als production
                  promoten (Admin).
                </Alert>
              ) : (
                <Alert severity="info">
                  Nach erfolgreichem Build kann ein Admin das Image auf der Status-Seite nach
                  production promoten.
                </Alert>
              )}
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button disabled={!showBack} onClick={handleBack} sx={{ visibility: showBack ? 'visible' : 'hidden' }}>
              Zurück
            </Button>
            {activeStep === 2 ? (
              <Button
                variant="contained"
                onClick={handleBuild}
                disabled={submitting || ownerCostCenter === ''}
                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {submitting ? 'Starte…' : dryRun ? 'Dry-Run starten' : 'Build starten'}
              </Button>
            ) : showNext ? (
              <Button variant="contained" onClick={handleNext} disabled={nextDisabled}>
                Weiter
              </Button>
            ) : (
              <Button variant="outlined" onClick={() => navigate('/workloads')}>
                Fertig
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
